import { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertTriangle } from 'lucide-react';
import { Card, CardBody } from './ui/Card';
import { Button } from './ui/Button';

interface Props {
  t: TFunction;
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the failure for diagnostics; the calm fallback handles the UI.
    console.error('ErrorBoundary caught an error', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { t, children } = this.props;
    if (!this.state.hasError) return children;

    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-paper dark:bg-ink-950">
        <Card className="max-w-md w-full">
          <CardBody className="text-center px-8 py-10">
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-warn-100 text-warn-600 dark:bg-warn-900/30">
              <AlertTriangle size={22} />
            </div>
            <h1 className="display text-xl font-semibold mb-2">{t('errors.boundaryTitle')}</h1>
            <p className="text-sm text-ink-500 mb-7">{t('errors.boundaryBody')}</p>
            <Button variant="primary" onClick={this.handleReload}>
              {t('errors.boundaryReload')}
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }
}

export function ErrorBoundary({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return <ErrorBoundaryInner t={t}>{children}</ErrorBoundaryInner>;
}
